import { useEffect, useId, useMemo, useRef, useState } from 'react';

const Select = ({
  name,
  options,
  value,
  onChange,
  placeholder = 'Select',
  disabled = false,
  searchable = false,
  searchPlaceholder = 'Search...',
  className = '',
  'aria-label': ariaLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const listId = useId();

  const selected = options.find((option) => option.value === value);
  const displayLabel = selected?.label ?? placeholder;
  const hasValue = Boolean(selected);

  const filteredOptions = useMemo(() => {
    if (!searchable || !query.trim()) return options;

    const needle = query.trim().toLowerCase();
    return options.filter((option) => {
      const haystack = `${option.searchText || ''} ${option.menuLabel || ''} ${option.label} ${option.value}`
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [options, query, searchable]);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      return undefined;
    }

    if (searchable) {
      const frame = requestAnimationFrame(() => searchRef.current?.focus());
      return () => cancelAnimationFrame(frame);
    }

    return undefined;
  }, [isOpen, searchable]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (disabled) setIsOpen(false);
  }, [disabled]);

  const choose = (nextValue) => {
    onChange(nextValue);
    setIsOpen(false);
  };

  return (
    <div
      ref={rootRef}
      className={`create-account__dropdown ${isOpen ? 'create-account__dropdown--open' : ''} ${
        disabled ? 'create-account__dropdown--disabled' : ''
      } ${className}`.trim()}
    >
      <input type="hidden" name={name} value={value} disabled={disabled} />

      <button
        type="button"
        className={`create-account__dropdown-trigger ${
          hasValue ? '' : 'create-account__dropdown-trigger--placeholder'
        }`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listId}
        aria-label={ariaLabel}
        disabled={disabled}
        tabIndex={disabled ? -1 : undefined}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span className="create-account__dropdown-value">{displayLabel}</span>
        <span className="create-account__dropdown-chevron" aria-hidden="true" />
      </button>

      {isOpen && (
        <div className="create-account__dropdown-panel">
          {searchable && (
            <div className="create-account__dropdown-search">
              <input
                ref={searchRef}
                type="text"
                className="create-account__dropdown-search-input"
                value={query}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    if (filteredOptions[0]) choose(filteredOptions[0].value);
                  }
                }}
              />
            </div>
          )}

          <ul id={listId} className="create-account__dropdown-menu" role="listbox">
            {filteredOptions.length === 0 ? (
              <li className="create-account__dropdown-empty" role="presentation">
                No results
              </li>
            ) : (
              filteredOptions.map((option) => (
                <li key={option.value} role="none">
                  <button
                    type="button"
                    role="option"
                    aria-selected={option.value === value}
                    className={`create-account__dropdown-option ${
                      option.value === value ? 'create-account__dropdown-option--selected' : ''
                    }`}
                    onClick={() => choose(option.value)}
                  >
                    {option.menuLabel || option.label}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Select;
